import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { UserProfile, TackleBoxItem, GearCategory, ToastType } from '../types';
import RoddoPageShell from './layout/RoddoPageShell';
import RoddoPageHero from './layout/RoddoPageHero';
import RoddoSectionCard from './layout/RoddoSectionCard';
import AddGearModal from './AddGearModal';
import RoddoConfirmDialog from './RoddoConfirmDialog';
import EditGearModal from './EditGearModal';
import { useRoutePortalTarget } from './layout/RoutePortalHost';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import {
  subscribeToGear,
  subscribeToEquippedGear,
  setEquippedGearSlot,
  deleteGearItem,
} from '../services/gear';

interface GearHubViewProps {
  userProfile: UserProfile | null;
  onManageTackleBox: () => void;
  onBack?: () => void;
  addToast?: (message: string, type?: ToastType) => void;
}

type EquippedSlot = 'rodId' | 'reelId' | 'lureOrBaitId';
type EquippedPatch = Partial<Record<EquippedSlot | 'lureId', string | null>>;

const normalizeEquippedForUi = (equipped: any): Record<string, any> => ({
  ...(equipped || {}),
  rodId: equipped?.rodId ?? null,
  reelId: equipped?.reelId ?? null,
  lureOrBaitId: equipped?.lureOrBaitId ?? equipped?.lureId ?? null,
  lureId: equipped?.lureId ?? equipped?.lureOrBaitId ?? null,
});

const applyPendingEquipped = (incoming: any, pending: EquippedPatch): Record<string, any> => {
  const normalized = normalizeEquippedForUi(incoming);
  return {
    ...normalized,
    ...pending,
    lureOrBaitId: pending.lureOrBaitId ?? normalized.lureOrBaitId ?? normalized.lureId ?? null,
    lureId: pending.lureId ?? pending.lureOrBaitId ?? normalized.lureId ?? normalized.lureOrBaitId ?? null,
  };
};

type GearTabId = 'rod' | 'reel' | 'baitsLures' | 'line' | 'smart' | 'other';

const tabOrder: GearTabId[] = ['rod', 'reel', 'baitsLures', 'line', 'smart', 'other'];

const tabLabels: Record<GearTabId, string> = {
  rod: 'Rods',
  reel: 'Reels',
  baitsLures: 'Baits',
  line: 'Line',
  smart: 'Smart',
  other: 'Other',
};

const tabIcons: Record<GearTabId, string> = {
  rod: '🎣',
  reel: '🌀',
  baitsLures: '🪱',
  line: '🧵',
  smart: '📡',
  other: '✨',
};

const gearCategoryEmoji = (category?: GearCategory | string | null): string => {
  if (category === 'rod') return '🎣';
  if (category === 'reel') return '🌀';
  if (category === 'lure') return '🪝';
  if (category === 'bait') return '🪱';
  if (category === 'line') return '🧵';
  if (category === 'smart') return '📡';
  return '✨';
};

const gearCategoryLabel = (category?: GearCategory | string | null): string => {
  if (category === 'rod') return 'Rod';
  if (category === 'reel') return 'Reel';
  if (category === 'lure') return 'Lure';
  if (category === 'bait') return 'Bait';
  if (category === 'line') return 'Line';
  if (category === 'smart') return 'Smart';
  return 'Gear';
};

const GearHubView: React.FC<GearHubViewProps> = ({
  userProfile,
  onManageTackleBox,
  onBack,
  addToast,
}) => {
  const { t } = useTranslation();
  const portalTarget = useRoutePortalTarget();
  const [gearToDelete, setGearToDelete] = useState<TackleBoxItem | null>(null);
  const [isDeletingGear, setIsDeletingGear] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveRoddoDarkMode());

  useEffect(() => {
    let lastTheme = resolveRoddoDarkMode();

    const syncTheme = () => {
      const nextTheme = resolveRoddoDarkMode();
      if (nextTheme !== lastTheme) {
        lastTheme = nextTheme;
        setIsDarkMode(nextTheme);
      }
    };

    setIsDarkMode(lastTheme);

    const media =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            window.requestAnimationFrame(syncTheme);
          })
        : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
      if (document.body) {
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-theme', 'style'],
        });
      }
    }

    window.addEventListener('rodo-theme-change', syncTheme as EventListener);
    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);
    window.addEventListener('focus', syncTheme as EventListener);
    window.addEventListener('pageshow', syncTheme as EventListener);
    window.addEventListener('visibilitychange', syncTheme as EventListener);
    media?.addEventListener?.('change', syncTheme);

    return () => {
      observer?.disconnect();
      window.removeEventListener('rodo-theme-change', syncTheme as EventListener);
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
      window.removeEventListener('focus', syncTheme as EventListener);
      window.removeEventListener('pageshow', syncTheme as EventListener);
      window.removeEventListener('visibilitychange', syncTheme as EventListener);
      media?.removeEventListener?.('change', syncTheme);
    };
  }, []);

  const isDark = isDarkMode;
  const ui = getRoddoUiTokens(isDark) as any;

  const [activeTab, setActiveTab] = useState<GearTabId>('rod');
  const [gearItems, setGearItems] = useState<TackleBoxItem[]>(() =>
    Array.isArray((userProfile as any)?.tackleBox) ? (((userProfile as any).tackleBox as TackleBoxItem[]) ?? []) : [],
  );
  const [gearLoaded, setGearLoaded] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [editItem, setEditItem] = useState<TackleBoxItem | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);

  const safeToast = (message: string, type: ToastType = 'info') => {
    if (addToast) addToast(message, type);
    else console.log(`[GearHubView:${type}] ${message}`);
  };

  useEffect(() => {
    const fallback = Array.isArray((userProfile as any)?.tackleBox)
      ? (((userProfile as any).tackleBox as TackleBoxItem[]) ?? [])
      : [];

    if (!userProfile?.uid) {
      setGearItems(fallback);
      setGearLoaded(true);
      return;
    }

    setGearItems(fallback);
    setGearLoaded(false);

    const unsub = subscribeToGear(
      userProfile.uid,
      (items) => {
        setGearItems(Array.isArray(items) ? items : []);
        setGearLoaded(true);
      },
      (err) => {
        console.error('[GearHubView] gear subscription failed', err);
        setGearLoaded(true);
      },
    );

    return () => unsub();
  }, [userProfile?.uid]);

  const [equippedLocal, setEquippedLocal] = useState<Record<string, any>>(
    () => normalizeEquippedForUi(userProfile?.equippedGear as any),
  );
  const pendingEquippedRef = useRef<EquippedPatch>({});

  useEffect(() => {
    if (!userProfile?.uid) {
      setEquippedLocal({});
      return;
    }

    const unsubscribe = subscribeToEquippedGear(
      userProfile.uid,
      (equipped) => {
        const normalized = normalizeEquippedForUi(equipped);
        pendingEquippedRef.current = Object.fromEntries(
          Object.entries(pendingEquippedRef.current).filter(([key, value]) => (normalized as any)[key] !== value),
        ) as EquippedPatch;
        setEquippedLocal(applyPendingEquipped(normalized, pendingEquippedRef.current));
      },
      (err) => console.error('[GearHubView] equipped gear subscription failed', err),
    );

    return () => unsubscribe();
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!openActionMenuId) return;

    const closeActionMenu = () => {
      setOpenActionMenuId(null);
      setActionMenuPos(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeActionMenu();
    };
    const handleScroll = () => closeActionMenu();
    const handleResize = () => closeActionMenu();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [openActionMenuId]);

  const closeActionMenu = () => {
    setOpenActionMenuId(null);
    setActionMenuPos(null);
  };

  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 190;
    const viewportPadding = 10;
    const left = Math.max(
      viewportPadding,
      Math.min(window.innerWidth - menuWidth - viewportPadding, rect.right - menuWidth),
    );
    const preferredTop = rect.bottom + 8;
    const top = Math.min(window.innerHeight - 176, Math.max(viewportPadding, preferredTop));

    if (openActionMenuId === itemId) {
      closeActionMenu();
      return;
    }

    setOpenActionMenuId(itemId);
    setActionMenuPos({ top, left });
  };

  const grouped = useMemo(() => {
    const byCategory: Record<GearCategory, TackleBoxItem[]> = {
      rod: [],
      reel: [],
      lure: [],
      bait: [],
      line: [],
      smart: [],
      other: [],
    };

    gearItems.forEach((item) => {
      const key = (item.category || 'other') as GearCategory;
      byCategory[key].push(item);
    });

    const totalGear = gearItems.length;
    const mostUsed = [...gearItems].sort((a, b) => (b.catches || 0) - (a.catches || 0))[0];
    const heaviestLure = [...byCategory.lure, ...byCategory.bait].sort(
      (a, b) => (b.weightOz || 0) - (a.weightOz || 0),
    )[0];

    const rodsCount = byCategory.rod.length;
    const reelsCount = byCategory.reel.length;
    const luresCount = byCategory.lure.length + byCategory.bait.length;

    const completenessScore = Math.min(
      100,
      Math.round(
        (rodsCount > 0 ? 20 : 0) +
          (reelsCount > 0 ? 20 : 0) +
          (luresCount > 2 ? 30 : 0) +
          (byCategory.line.length > 0 ? 15 : 0) +
          (byCategory.smart.length > 0 ? 15 : 0),
      ),
    );

    return {
      byCategory,
      totalGear,
      mostUsed,
      heaviestLure,
      completenessScore,
    };
  }, [gearItems]);

  const equipped = equippedLocal || {};

  const resolveSlotForCategory = (
    category: GearCategory,
  ): EquippedSlot | null => {
    if (category === 'rod') return 'rodId';
    if (category === 'reel') return 'reelId';
    if (category === 'lure' || category === 'bait') return 'lureOrBaitId';
    return null;
  };

  const activeItems = useMemo(() => {
    if (activeTab === 'baitsLures') {
      return [...grouped.byCategory.bait, ...grouped.byCategory.lure];
    }

    const tabToCategory: Record<Exclude<GearTabId, 'baitsLures'>, GearCategory> = {
      rod: 'rod',
      reel: 'reel',
      line: 'line',
      smart: 'smart',
      other: 'other',
    };

    return grouped.byCategory[tabToCategory[activeTab as Exclude<GearTabId, 'baitsLures'>]] || [];
  }, [activeTab, grouped.byCategory]);

  const handleEquip = async (category: GearCategory, item: TackleBoxItem) => {
    const slot = resolveSlotForCategory(category);
    if (!slot || !userProfile?.uid || !item.id) return;

    if ((equippedLocal as any)?.[slot] === item.id) return;

    const previous = { ...(equippedLocal || {}) };
    const pendingPatch: EquippedPatch =
      slot === 'lureOrBaitId'
        ? { lureOrBaitId: item.id, lureId: item.id }
        : { [slot]: item.id };

    pendingEquippedRef.current = {
      ...pendingEquippedRef.current,
      ...pendingPatch,
    };

    setEquippedLocal((prev) => applyPendingEquipped(prev, pendingPatch));

    try {
      await setEquippedGearSlot(userProfile.uid, slot, item.id);
      setEquippedLocal((prev) => applyPendingEquipped(prev, pendingPatch));
      // Launch quiet pass: active-kit state updates visually; no routine equip toast needed.
    } catch (err) {
      console.error('[GearHubView] Failed to equip gear:', err);
      Object.keys(pendingPatch).forEach((key) => delete (pendingEquippedRef.current as any)[key]);
      setEquippedLocal(previous);
      safeToast(t('gear.equip.error', 'Could not equip this item. Please try again.'), 'error');
    }
  };

  const handleDeleteGear = (item: TackleBoxItem) => {
    if (!userProfile?.uid || !item.id) return;
    closeActionMenu();
    setGearToDelete(item);
  };

  const confirmDeleteGear = async () => {
    const item = gearToDelete;
    if (!userProfile?.uid || !item?.id) return;

    const previousEquipped = { ...(equippedLocal || {}) };
    setIsDeletingGear(true);

    setEquippedLocal((prev) => {
      const next = { ...(prev || {}) };

      if (next.rodId === item.id) next.rodId = null;
      if (next.reelId === item.id) next.reelId = null;

      const currentLureOrBaitId = next.lureOrBaitId ?? next.lureId ?? null;
      if (currentLureOrBaitId === item.id || next.lureId === item.id) {
        next.lureOrBaitId = null;
        next.lureId = null;
      }

      return next;
    });

    try {
      await deleteGearItem(userProfile.uid, item.id);
      setGearToDelete(null);
      safeToast(t('gear.delete.success', 'Gear item deleted.'), 'success');
    } catch (err) {
      console.error('[GearHubView] Failed to delete gear:', err);
      setEquippedLocal(previousEquipped);
      safeToast(t('gear.delete.error', 'Could not delete this gear item.'), 'error');
    } finally {
      setIsDeletingGear(false);
    }
  };

  const handleGearAdded = () => {
    // AddGearModal already owns the success toast. Avoid duplicate confirmations.
    setIsAddModalOpen(false);
  };

  const closeEdit = () => {
    closeActionMenu();
    setEditItem(null);
  };


  const shellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDark
      ? "0 8px 20px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.025)"
      : "0 14px 30px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.66)",
    color: ui.textPrimary,
  };
  const sectionStyle: React.CSSProperties = {
    background: ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: isDark ? '0 5px 14px rgba(0,0,0,0.13)' : `${ui.shadow}, inset 0 2px 0 rgba(255,255,255,0.18)`,
    color: ui.textPrimary,
  };
  const rowStyle: React.CSSProperties = {
    background: ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.12)' : `${ui.shadow}, inset 0 2px 0 rgba(255,255,255,0.16)`,
    color: ui.textPrimary,
  };
  const inputStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.insetHighlight}, inset 0 2px 0 rgba(255,255,255,0.14)`,
  };
  const subtleButtonStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.18)`,
  };
  const manageButtonStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: ui.shadow,
  };
  const addButtonStyle: React.CSSProperties = {
    background: isDark
      ? '#67D8E8'
      : 'linear-gradient(180deg,#5B8DFF 0%,#2B6CFF 100%)',
    color: '#FFFFFF',
    boxShadow: isDark
      ? '0 6px 14px rgba(20,145,166,0.16)'
      : '0 8px 18px rgba(43,108,255,0.20)',
  };
  const equippedButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(52,211,153,0.16)' : '#ECFFF6',
    color: isDark ? '#A7F3D0' : '#0F6B49',
    border: `1px solid ${isDark ? 'rgba(52,211,153,0.22)' : 'rgba(15,107,73,0.16)'}`,
    boxShadow: 'none',
  };
  const editButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(102,217,232,0.10)' : '#F5F1FF',
    color: isDark ? '#BDEFF5' : '#4C3FA8',
    border: `1px solid ${isDark ? 'rgba(102,217,232,0.18)' : 'rgba(76,63,168,0.12)'}`,
    boxShadow: 'none',
  };
  const deleteButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(251,113,133,0.14)' : 'rgba(255,228,230,0.96)',
    color: '#be123c',
    border: `1px solid ${isDark ? 'rgba(251,113,133,0.24)' : 'rgba(251,113,133,0.18)'}`,
  };

  const openActionItem = useMemo(
    () => gearItems.find((item) => item.id === openActionMenuId) || null,
    [gearItems, openActionMenuId],
  );

  const renderGearRows = (items: TackleBoxItem[]) => {
    if (!gearLoaded) {
      return (
        <p className="text-xs" style={{ color: ui.textSecondary }}>
          {t('gear.loading', 'Loading your saved gear…')}
        </p>
      );
    }

    if (!items.length) {
      return (
        <div
          className="rounded-[22px] px-4 py-4 text-sm"
          style={{ background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg, border: `1px solid ${ui.border}`, color: ui.textSecondary, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)' }}
        >
          <div className="mb-1 text-2xl" aria-hidden="true">{tabIcons[activeTab]}</div>
          <p className="font-semibold">{t('gear.noneYet', 'Nothing saved here yet. Add some gear in your The Hold!')}</p>
          <p className="mt-1 text-xs opacity-80">{t('gearHub.emptyHint', 'Tap Add Gear to stash the next piece.')}</p>
        </div>
      );
    }

    return (
      <ul className="flex flex-col gap-3">
        {(items ?? []).map((item) => {
          const itemCategory = (item.category || 'other') as GearCategory;
          const slot = resolveSlotForCategory(itemCategory);
          const currentLureOrBaitId = equipped.lureOrBaitId ?? equipped.lureId ?? null;
          const isEquipped =
            slot === 'lureOrBaitId'
              ? currentLureOrBaitId === item.id
              : !!slot && equipped[slot] === item.id;

          const isMenuOpen = openActionMenuId === item.id;

          return (
            <li
              key={item.id}
              className="roddo-gear-row roddo-gear-transition flex flex-col gap-3 rounded-[24px] px-3 py-3 min-[390px]:px-4 min-[390px]:py-4"
              style={{
                ...rowStyle,
                ...(isEquipped ? { border: '1px solid rgba(52,211,153,0.45)' } : {}),
              }}
            >
              <div className="grid min-w-0 w-full grid-cols-[54px_minmax(0,1fr)_40px] items-start gap-3 min-[390px]:grid-cols-[64px_minmax(0,1fr)_40px]">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="h-[54px] w-[54px] shrink-0 rounded-[18px] object-cover min-[390px]:h-16 min-[390px]:w-16 min-[390px]:rounded-[22px]"
                    style={{ border: `1px solid ${ui.border}` }}
                  />
                ) : (
                  <div
                    className="roddo-gear-thumb-placeholder roddo-gear-transition flex h-[54px] w-[54px] shrink-0 flex-col items-center justify-center rounded-[18px] border border-dashed text-[9px] font-bold min-[390px]:h-16 min-[390px]:w-16 min-[390px]:rounded-2xl min-[390px]:text-[10px]"
                    style={{ borderColor: ui.border, color: ui.textSecondary, background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg }}
                  >
                    <span className="text-xl" aria-hidden="true">{gearCategoryEmoji(itemCategory)}</span>
                    <span className="mt-0.5 text-[9px] font-black uppercase tracking-[0.12em]">{t(`gear.categories.${String(itemCategory || 'gear')}`, gearCategoryLabel(itemCategory))}</span>
                  </div>
                )}

                <div className="min-w-0">
                  <p className="truncate text-sm font-extrabold" style={{ color: ui.textPrimary }}>
                    {item.name}
                  </p>

                  {item.brand || item.model ? (
                    <p className="truncate text-[11px]" style={{ color: ui.textSecondary }}>
                      {item.brand || ''}
                      {item.brand && item.model ? ' • ' : ''}
                      {item.model || ''}
                    </p>
                  ) : null}

                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.10em]">
                    <span
                      className="rounded-full px-2 py-1"
                      style={{ background: isDark ? 'rgba(148,166,201,0.07)' : 'rgba(255,246,224,0.92)', color: ui.textSecondary, border: `1px solid ${ui.border}` }}
                    >
                      {gearCategoryEmoji(itemCategory)} {t(`gear.categories.${String(itemCategory || 'gear')}`, gearCategoryLabel(itemCategory))}
                    </span>
                    {isEquipped ? (
                      <span
                        className="rounded-full px-2 py-1"
                        style={{ background: 'rgba(52,211,153,0.16)', color: isDark ? '#86efac' : '#047857', border: '1px solid rgba(52,211,153,0.26)' }}
                      >
                        {t('gear.activeKit', 'Active kit')}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                    {typeof item.weightOz === 'number' && item.weightOz > 0 ? (
                      <span className="font-black" style={{ color: '#2B6CFF' }}>
                        {item.weightOz.toFixed(2)} oz
                      </span>
                    ) : null}

                    {typeof item.value === 'number' && Number.isFinite(item.value) ? (
                      <span className="font-semibold" style={{ color: '#16a34a' }}>
                        ${item.value.toFixed(2)}
                      </span>
                    ) : null}
                  </div>

                  {item.notes ? (
                    <p className="mt-1 line-clamp-2 text-[11px]" style={{ color: ui.textSecondary }}>
                      {item.notes}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={(event) => item.id && openActionMenu(event, item.id)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-black leading-none transition active:scale-95"
                  style={subtleButtonStyle}
                  aria-label={t('common.more', 'More') as string}
                  aria-expanded={isMenuOpen}
                >
                  …
                </button>
              </div>

            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <RoddoPageShell
      padding="md"
      contentClassName="max-w-4xl"
      className="roddo-gear-hub roddo-hold-polish overflow-x-hidden"
    >
      <div data-roddo-theme={isDark ? 'dark' : 'light'} data-roddo-polish-lane="2">
        <RoddoPageHero
          tone="gear"
          title={t('gear.hero.title', 'Your fishing loadout')}
          subtitle={t(
            'gear.hero.subtitle',
            'Tune rods, reels, lures, line, and smart gear before the next trip. The Hold keeps the kit organized and ready.',
          )}
          icon="🎒"
          isDarkMode={isDark}
          topRightAction={
            onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={t('common.back', 'Back') as string}
                title={t('common.back', 'Back') as string}
                className="roddo-gear-chip-button roddo-gear-transition flex h-10 w-10 items-center justify-center rounded-full text-xl font-black leading-none"
                style={subtleButtonStyle}
              >
                ‹
              </button>
            ) : null
          }
          stats={[
            { label: `🎒 ${t('gear.totalGear', 'Total gear')}`, value: grouped.totalGear },
            { label: `🧰 ${t('gear.loadoutScore', 'Loadout')}`, value: `${grouped.completenessScore}/100` },
            { label: `⭐ ${t('gear.mostUsed', 'Most used')}`, value: grouped.mostUsed ? grouped.mostUsed.name : t('gear.none', 'None yet') },
            {
              label: `⚖️ ${t('gear.heaviestLure', 'Heaviest lure')}`,
              value: grouped.heaviestLure ? grouped.heaviestLure.name : t('gear.none', 'None yet'),
              helper: grouped.heaviestLure?.weightOz ? `${grouped.heaviestLure.weightOz.toFixed(2)} oz` : undefined,
            },
          ]}
          actions={(
            <>
              <button
                type="button"
                onClick={onManageTackleBox}
                className="roddo-gear-chip-button roddo-gear-transition rounded-full px-4 py-2 text-[11px] font-extrabold tracking-[-0.01em]"
                style={manageButtonStyle}
              >
                🧭 {t('gear.manage', 'Manage')}
              </button>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="roddo-gear-add-button roddo-gear-transition rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em]"
                style={addButtonStyle}
              >
                {t('gear.addGear', '✨ Add gear')}
              </button>
            </>
          )}
        >
          <div className="h-2 overflow-hidden rounded-full border border-black/10 bg-white/50 dark:border-white/10 dark:bg-black/10">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isDark ? "bg-[#67D8E8]" : "bg-[#2F80ED]"}`}
              style={{ width: `${Math.max(6, grouped.completenessScore)}%` }}
            />
          </div>
        </RoddoPageHero>



      <RoddoSectionCard>
        <div className="roddo-gear-shell roddo-gear-transition rounded-[28px] p-4" style={sectionStyle}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>{t('gearHub.kitShelf', '🧃 Kit shelf')}</p>
              <h3 className="text-base font-extrabold" style={{ color: ui.textPrimary }}>{tabIcons[activeTab]} {t(`gear.tabs.${activeTab}`, tabLabels[activeTab])}</h3>
            </div>
            <span className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em]" style={{ background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg, color: ui.textSecondary, border: `1px solid ${ui.border}` }}>
              {t('gear.savedCount', '{{count}} saved', { count: activeItems.length })}
            </span>
          </div>
          <div className="-mx-1 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]">
            <div className="flex min-w-max gap-2 px-1" role="tablist" aria-label="Gear categories">
              {tabOrder.map((id) => {
                const selected = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setActiveTab(id)}
                    className={[
                      'roddo-gear-tab roddo-gear-transition relative rounded-[11px] px-3.5 py-2 text-center whitespace-nowrap',
                      selected ? 'is-active' : '',
                    ].join(' ')}
                    style={{
                      background: selected
                        ? (isDark ? 'rgba(138,112,242,0.10)' : 'rgba(232,243,255,0.92)')
                        : 'transparent',
                      color: selected ? (isDark ? '#D8D0FF' : '#2E6FAD') : ui.textSecondary,
                      border: `1px solid ${selected ? ui.borderStrong ?? ui.border : 'transparent'}`,
                      boxShadow: selected ? (isDark ? 'inset 0 -2px 0 #8A70F2' : 'inset 0 -2px 0 #2F80ED') : 'none',
                    }}
                  >
                    <span className="flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-[0.12em]">
                      <span aria-hidden="true">{tabIcons[id]}</span>
                      {t(`gear.tabs.${id}`, tabLabels[id])}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4">{renderGearRows(activeItems)}</div>
        </div>
      </RoddoSectionCard>

      {typeof document !== 'undefined' && openActionItem && actionMenuPos && portalTarget
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[99998]" onClick={closeActionMenu} />
              <div
                className="fixed z-[99999] w-[190px] overflow-hidden rounded-[18px] p-1.5"
                style={{
                  top: actionMenuPos.top,
                  left: actionMenuPos.left,
                  background: ui.cardBgStrong ?? ui.cardBg,
                  border: `1px solid ${ui.borderStrong ?? ui.border}`,
                  boxShadow: ui.shadowStrong ?? '0 18px 42px rgba(15,23,42,.20)',
                }}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-label="Gear actions"
              >
                <div className="border-b px-3 py-2.5" style={{ borderColor: ui.border }}>
                  <div className="truncate text-[11px] font-black" style={{ color: ui.textPrimary }}>{openActionItem.name}</div>
                  <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[.11em]" style={{ color: ui.textSecondary }}>
                    {gearCategoryLabel(openActionItem.category)} actions
                  </div>
                </div>
                <div className="space-y-1 p-1.5">
                  {resolveSlotForCategory((openActionItem.category || 'other') as GearCategory) ? (
                    <button
                      type="button"
                      onClick={() => {
                        const category = (openActionItem.category || 'other') as GearCategory;
                        closeActionMenu();
                        void handleEquip(category, openActionItem);
                      }}
                      className="w-full rounded-[12px] px-3 py-2.5 text-left text-[11px] font-black transition active:scale-[.98]"
                      style={
                        (() => {
                          const category = (openActionItem.category || 'other') as GearCategory;
                          const slot = resolveSlotForCategory(category);
                          const currentLureOrBaitId = equipped.lureOrBaitId ?? equipped.lureId ?? null;
                          const isEquipped = slot === 'lureOrBaitId'
                            ? currentLureOrBaitId === openActionItem.id
                            : !!slot && equipped[slot] === openActionItem.id;
                          return isEquipped ? equippedButtonStyle : subtleButtonStyle;
                        })()
                      }
                    >
                      {(() => {
                        const category = (openActionItem.category || 'other') as GearCategory;
                        const slot = resolveSlotForCategory(category);
                        const currentLureOrBaitId = equipped.lureOrBaitId ?? equipped.lureId ?? null;
                        const isEquipped = slot === 'lureOrBaitId'
                          ? currentLureOrBaitId === openActionItem.id
                          : !!slot && equipped[slot] === openActionItem.id;
                        return isEquipped ? '✓ Equipped' : '✓ Equip';
                      })()}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      const item = openActionItem;
                      closeActionMenu();
                      setEditItem(item);
                    }}
                    className="w-full rounded-[12px] px-3 py-2.5 text-left text-[11px] font-black transition active:scale-[.98]"
                    style={editButtonStyle}
                  >
                    ✏️ Edit gear
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteGear(openActionItem)}
                    className="w-full rounded-[12px] px-3 py-2.5 text-left text-[11px] font-black transition active:scale-[.98]"
                    style={deleteButtonStyle}
                  >
                    🗑 Delete gear
                  </button>
                </div>
              </div>
            </>,
            portalTarget,
          )
        : null}

      <AddGearModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdded={handleGearAdded}
        userProfile={userProfile}
        addToast={addToast}
      />

      <EditGearModal
        open={!!editItem}
        item={editItem}
        userProfile={userProfile}
        onClose={closeEdit}
        onSaved={() => setEditItem(null)}
        addToast={addToast}
      />

      </div>
      <RoddoConfirmDialog
        open={!!gearToDelete}
        title={t('gear.delete.title', 'Delete gear item?') as string}
        description={t('gear.delete.confirm', 'This permanently removes the saved gear item from The Hold.') as string}
        confirmLabel={t('common.delete', 'Delete') as string}
        cancelLabel={t('common.cancel', 'Cancel') as string}
        tone="danger"
        isBusy={isDeletingGear}
        isDarkMode={isDarkMode}
        onConfirm={confirmDeleteGear}
        onCancel={() => { if (!isDeletingGear) setGearToDelete(null); }}
      />
    </RoddoPageShell>
  );
};

export default GearHubView;
  